import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash-es';
import { Table, SelectOption } from 'component-library-and-tools';

import SearchBar from 'shared/components/SearchBar';
import MultiSelect from 'shared/components/MultiSelect';
import Checkbox from 'shared/components/Checkbox';
import { IndexableObjectType } from 'shared/types/common';
import { Source } from 'shared/types/source';
import { formatDate } from 'shared/utils';
import { getSourceEnclaves } from 'services/source/getters';
import { getThemeMode } from 'services/user/getters';
import { colors, colorsByTheme, Theme } from 'lib/prisma-styles';

import ViewSourceModal from '../../ViewSourceModal';

import { WeightHeaderCell } from './headerCells/WeightHeaderCell';
import SourceSelectionCell from './cells/SourceSelectionCell';
import SourceNameCell from './cells/SourceNameCell';
// import SourceStatusCell from './cells/SourceStatusCell';
import SourceTypeCell from './cells/SourceTypeCell';
import SourceWeightCell from './cells/SourceWeightCell';
import ViewSourceCell from './cells/ViewSourceCell';
// import StatusFilterOption from './filters/StatusFilterOption';
import { SourceSelectionProps } from './types';
import {
  SEARCH_FILTER_NAME,
  TYPE_FILTER_NAME,
  STATUS_FILTER_NAME,
  TYPE_FILTER_OPTIONS,
  SOURCE_COUNT_LIMIT,
  // STATUS_FILTER_OPTIONS,
} from './constants';

const TABLE_HEADER_ROW_CLASS_NAME = 'destination__table-header-row';
const TABLE_HEADER_CELL_CLASS_NAME = 'destination__table-header-cell';
const TABLE_ROW_CLASS_NAME = 'destination__table-row';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const StyledFilterContainer = styled.div`
  display: flex;
  align-items: center;
  height: 3.666666667rem;
  margin-bottom: 1.333333333rem;
`;

const commonFilterStyles = css`
  height: 100%;
  font-size: 1.166666667rem;

  &:not(:last-child) {
    margin-right: 1rem;
  }
`;

const StyledSearchFilter = styled(SearchBar)`
  flex-basis: 87.70%;
  ${commonFilterStyles}
`;

const StyledTypeFilter = styled(MultiSelect)`
  flex-basis: 12.30%;
  ${commonFilterStyles}
`;

// const StyledStatusFilter = styled(MultiSelect)`
//   flex-basis: 12.30%;
//   ${commonFilterStyles}
// `;

const StyledTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const StyledTable = styled(Table)`
  && {
    min-height: 0;
    font-size: 1.166666667rem;

    .${TABLE_HEADER_ROW_CLASS_NAME} {
      border-color: #8d9199;
    }

    .${TABLE_ROW_CLASS_NAME} {
      height: 4rem;
      border-bottom-color: ${colors.interactive.defaultBorder};
      cursor: pointer;
    }
  }
`;

const filterBySourceName = (sourceList: Source[], sourceName: string) => (
  sourceList.filter(({ name }) => new RegExp(sourceName, 'ig').test(name || ''))
);

const filterBySourceTypes = (sourceList: Source[], sourceTypes: SelectOption[]) => {
  if (isEmpty(sourceTypes)) {
    return sourceList;
  }

  return sourceList.filter(({ type }) => !!sourceTypes.find(({ name }) => name === type));
};

const filterBySourceStatus = (sourceList: Source[], sourceStatus: SelectOption[]) => {
  if (isEmpty(sourceStatus)) {
    return sourceList;
  }

  return sourceList.filter(({ status }) => !!sourceStatus.find(({ name }) => name === status));
};

const FILTER_MAPPER: IndexableObjectType = {
  [SEARCH_FILTER_NAME]: filterBySourceName,
  [TYPE_FILTER_NAME]: filterBySourceTypes,
  [STATUS_FILTER_NAME]: filterBySourceStatus,
};

const filterSources = (sourceList: Source[], filters: Object) => {
  let filteredSources = sourceList;

  Object.entries(filters).forEach(([key, value]) => {
    const filterFn = FILTER_MAPPER[key];

    filteredSources = filterFn(filteredSources, value);
  });

  return filteredSources;
};

const updateFilters = (
  filterKey: string,
  filterValue: any,
  currentFilters: Object,
  setFilters: React.Dispatch<React.SetStateAction<Object>>,
) => {
  setFilters({
    ...currentFilters,
    [filterKey]: filterValue,
  });
};

const onHeaderCheckboxClick = (
  isChecked: boolean,
  sources: Source[],
  onChange: (value: any, fieldName?: string) => void,
) => {
  if (isChecked) {
    // Newly selected source should have a weight of 1
    onChange(sources.map(source => {
      source.weight = source.weight || 1;
      return source;
    }));
  } else {
    onChange([]);
  }
};

const onTableRowClick = (
  selectedSource: Source,
  selectedSources: Source[],
  onChange: (value: any, fieldName?: string) => void,
) => {
  const found = !!selectedSources.find(({ id }) => id === selectedSource.id);

  // See if the clicked source has already been selected otherwise initialize a new source with default weight
  if (found) {
    onChange(selectedSources.filter(({ id }) => id !== selectedSource.id));
  } else {
    if (selectedSources.length >= SOURCE_COUNT_LIMIT){
      return;
    }
    //  Init new source
    const newlySelectedSource: Source = {
      ...selectedSource,
      weight: 1,
    };
    
    onChange(selectedSources.concat(newlySelectedSource));
  }
};

const getSourceList = (sources: Source[], selectedSources: Source[]) => sources.map((source) => {
  const selectedSource = selectedSources.find(({ id }) => source.id === id);

  return selectedSource || source;
});

const SourceSelection = ({
  onChange,
  values: selectedSources,
}: SourceSelectionProps) => {
  const [filters, setFilters] = useState({});
  const availableSources = useSelector(getSourceEnclaves);
  const theme: keyof Theme = useSelector(getThemeMode);
  const sourceList = getSourceList(availableSources, selectedSources);
  const filteredSources = filterSources(sourceList, filters);

  return (
    <StyledContainer>
      <StyledFilterContainer>
        <StyledSearchFilter
          placeholder='Search sources...'
          onSearch={(searchValue) => (
            updateFilters(SEARCH_FILTER_NAME, searchValue, filters, setFilters)
          )}
        />
        <StyledTypeFilter
          menuGap={4}
          options={TYPE_FILTER_OPTIONS}
          placeholder='Select type'
          allSelectedLabel='Type: All'
          onSelect={(selectedTypes) => (
            updateFilters(TYPE_FILTER_NAME, selectedTypes, filters, setFilters)
          )}
        />
        {/* Commented until we support it */}
        {/* <StyledStatusFilter
          menuGap={4}
          options={STATUS_FILTER_OPTIONS}
          optionComponent={StatusFilterOption}
          placeholder='Select status'
          allSelectedLabel='Status: All'
          onSelect={(selectedStatus) => (
            updateFilters(STATUS_FILTER_NAME, selectedStatus, filters, setFilters)
          )}
        /> */}
      </StyledFilterContainer>
      <StyledTableContainer>
        <StyledTable
          headerRowClassName={TABLE_HEADER_ROW_CLASS_NAME}
          headerCellClassName={TABLE_HEADER_CELL_CLASS_NAME}
          rowClassName={TABLE_ROW_CLASS_NAME}
          // NOTE: This is temporary until we refactor the table to allow custom rows.
          buildBodyRowStyles={(source) => {
            return !!selectedSources.find(({ id }) => id === source.id)
              ? { backgroundColor: colorsByTheme[theme].interactive.selectedActive }
              : undefined
          }}
          rowData={filteredSources}
          columns={[
            {
              name: 'sourceSelection',
              headerLabel: '',
            //   headerCellComponent: Checkbox,
            //   headerCellComponentExtraProps: {
            //     isChecked: !isEmpty(selectedSources) && filteredSources.every(({ id: filteredSourceId }) => (
            //       selectedSources.find(({ id: selectedSourceId }) => selectedSourceId === filteredSourceId)
            //     )),
            //     onChange: (isChecked: boolean) => onHeaderCheckboxClick(isChecked, filteredSources, onChange),
            //   },
              dataAccessor: 'id',
              cellComponent: SourceSelectionCell,
              cellComponentExtraProps: { selectedSources },
              width: '4%',
              style: { maxWidth: 48 },
            },
            {
              name: 'sourceName',
              dataAccessor: (source) => source,
              cellComponent: SourceNameCell,
              width: '25%',
            },
            {
              name: 'lastSubmitted',
              dataAccessor: (source) => formatDate(source.lastSubmitted),
              width: '12%',
            },
            {
              name: 'subscribed',
              dataAccessor: (source) => formatDate(source.subscribed),
              width: '12%',
            },
            {
              name: 'type',
              dataAccessor: 'type',
              cellComponent: SourceTypeCell,
              width: '17%',
            },
            // Commented until we support it.
            // {
            //   name: 'status',
            //   dataAccessor: 'status',
            //   cellComponent: SourceStatusCell,
            //   width: '12.61%',
            // },
            {
              name: 'weight',
              headerCellComponent: WeightHeaderCell,
              dataAccessor: (source) => source,
              cellComponent: SourceWeightCell,
              cellComponentExtraProps: {
                selectedSources,
                onChange,
              },
              width: '10%',
            },
            {
              name: 'sourceViewer',
              dataAccessor: (source) => source,
              cellComponent: ViewSourceCell,
              headerLabel: '',
              width: '5%',
              style: { maxWidth: 48 },
            },
          ]}
          onRowClick={(selectedSource) => onTableRowClick(selectedSource, selectedSources, onChange)}
          noDataMessage='No search results.'
        />
      </StyledTableContainer>
      <ViewSourceModal />
    </StyledContainer>
  );
};

export default SourceSelection;
